import React from 'react';
import { GoogleAuthProvider, signInWithPopup, auth, firebaseConfig } from '../firebase';
import { translations, Language } from './landingTranslations';
import { AnimatedStoryModal } from './AnimatedStoryModal';

interface LandingPageProps {
  onSignInClick?: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = () => {
  const [lang, setLang] = React.useState<Language>('en');
  const [isSigningIn, setIsSigningIn] = React.useState(false);
  const [signInError, setSignInError] = React.useState<string | null>(null);
  const [activeFaq, setActiveFaq] = React.useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [activeShowcaseTab, setActiveShowcaseTab] = React.useState<'attendance' | 'homework' | 'broadcast'>('attendance');
  const [isStoryOpen, setIsStoryOpen] = React.useState(false);

  const t = translations[lang];

  // Curated, fast, educational and community visuals
  const images = {
    heroClassroom: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=1200&q=80", // Bright modern school classroom with sunlight
    principalPortrait: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=800&q=80", // Confident, warm Indian educational leader/principal with books
    teacherHelping: "https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&w=800&q=80", // Caring school teacher guiding elementary/middle school students
    parentChildHome: "https://images.unsplash.com/photo-1606092195730-5d7b9af1efc5?auto=format&fit=crop&w=800&q=80", // Caring Indian parent helping child with notebook & homework at home
    schoolBuilding: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=800&q=80", // Beautiful collegiate/academy school campus
    studentsHighFive: "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=800&q=80", // Joyful diverse students in school uniform learning
  };

  // Direct Google Sign-In Trigger
  const handleGoogleSignIn = async () => {
    if (firebaseConfig.apiKey.startsWith('REPLACE_WITH')) {
      alert("SETUP INCOMPLETE: Please ensure Firebase configuration is set.");
      return;
    }

    setIsSigningIn(true);
    setSignInError(null);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') {
        setIsSigningIn(false);
        return;
      }
      console.error("Google Sign-In failed:", error);
      let msg = lang === 'hi' ? "लॉग इन पूरा नहीं हो सका। कृपया इंटरनेट जांचें और पुनः प्रयास करें।" : "Could not complete sign-in. Please check your network and try again.";
      if (error.code === 'auth/unauthorized-domain') {
        msg = `Domain not authorized in Firebase Auth. Add ${window.location.hostname} to authorized domains.`;
      } else if (error.code === 'auth/popup-blocked') {
        msg = lang === 'hi' ? "ब्राउज़र द्वारा पॉपअप ब्लॉक कर दिया गया। कृपया अनुमति दें।" : "Popup was blocked by your browser. Please allow popups to sign in.";
      }
      setSignInError(msg);
      setIsSigningIn(false);
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const faqs = [
    { q: t.faq.q1, a: t.faq.a1 },
    { q: t.faq.q2, a: t.faq.a2 },
    { q: t.faq.q3, a: t.faq.a3 },
    { q: t.faq.q4, a: t.faq.a4 },
    { q: t.faq.q5, a: t.faq.a5 },
    { q: t.faq.q6, a: t.faq.a6 },
    { q: t.faq.q7, a: t.faq.a7 }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-indigo-600 selection:text-white">
      
      {/* 1. CLEAN, RESPONSIVE TOP HEADER WITH LANGUAGE TOGGLE */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-18 flex items-center justify-between">
          
          {/* Clean Brand Logo */}
          <div 
            className="flex items-center gap-2.5 cursor-pointer select-none flex-shrink-0" 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-indigo-500 flex items-center justify-center text-white shadow-md shadow-indigo-600/20 flex-shrink-0">
              <span className="material-icons-round text-2xl">school</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-black tracking-tight text-slate-900 leading-none">
                EduGravity
              </span>
              <span className="hidden sm:inline-block text-[10px] font-semibold text-slate-400 tracking-wide mt-1">
                School & Coaching OS
              </span>
            </div>
          </div>

          {/* Clean Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center lg:gap-6 xl:gap-8 text-sm font-semibold text-slate-600">
            <button onClick={() => scrollToSection('features')} className="hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">
              {t.nav.features}
            </button>
            <button onClick={() => scrollToSection('community')} className="hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">
              {t.nav.forSchools}
            </button>
            <button onClick={() => scrollToSection('how-it-works')} className="hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">
              {t.nav.howItWorks}
            </button>
            <button onClick={() => scrollToSection('faq')} className="hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">
              {t.nav.faq}
            </button>
            <button onClick={() => scrollToSection('contact')} className="hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">
              {t.nav.contact}
            </button>
          </nav>

          {/* Desktop & Mobile Actions */}
          <div className="flex items-center gap-2.5 sm:gap-3 flex-shrink-0">
            
            {/* Minimalist Language Switcher Segment (English / हिन्दी) */}
            <div className="flex items-center bg-slate-100/90 p-0.5 rounded-lg border border-slate-200/80 text-[11px] font-bold text-slate-600 flex-shrink-0">
              <button
                type="button"
                onClick={() => setLang('en')}
                className={`px-2 py-0.5 rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  lang === 'en' 
                    ? 'bg-white text-indigo-600 shadow-2xs font-bold' 
                    : 'text-slate-500 hover:text-slate-800'
                }`}
                title="Switch to English"
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setLang('hi')}
                className={`px-2 py-0.5 rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  lang === 'hi' 
                    ? 'bg-white text-indigo-600 shadow-2xs font-bold' 
                    : 'text-slate-500 hover:text-slate-800'
                }`}
                title="हिंदी में बदलें"
              >
                हिन्दी
              </button>
            </div>

            {/* Desktop "Sign In" link */}
            <button
              onClick={handleGoogleSignIn}
              disabled={isSigningIn}
              className="hidden sm:inline-flex text-xs sm:text-sm font-semibold text-slate-600 hover:text-indigo-600 px-2 sm:px-2.5 py-1.5 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t.nav.signIn}
            </button>

            {/* Prominent "Start Free" CTA */}
            <button
              onClick={handleGoogleSignIn}
              disabled={isSigningIn}
              className="px-3.5 sm:px-4.5 py-2 sm:py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm font-bold shadow-sm shadow-indigo-600/20 hover:shadow-indigo-600/30 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 whitespace-nowrap flex-shrink-0"
            >
              {isSigningIn ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span className="hidden sm:inline">{t.nav.signingIn}</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <span>{t.nav.startFree}</span>
                  <span className="material-icons-round text-base">arrow_forward</span>
                </>
              )}
            </button>

            {/* Mobile Hamburger Toggle Button */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors focus:outline-hidden"
              aria-label="Toggle Menu"
            >
              <span className="material-icons-round text-2xl">
                {mobileMenuOpen ? 'close' : 'menu'}
              </span>
            </button>

          </div>
        </div>

        {/* Mobile Slide-down Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-b border-slate-200 px-5 pt-3 pb-6 space-y-3 shadow-lg animate-fadeIn">
            
            {/* Mobile Language Switcher */}
            <div className="flex items-center justify-between py-2 border-b border-slate-100 mb-2">
              <span className="text-xs font-bold text-slate-500 flex items-center gap-1.5">
                <span className="material-icons-round text-base text-indigo-600">translate</span>
                <span>Language / भाषा:</span>
              </span>
              <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-bold">
                <button
                  type="button"
                  onClick={() => setLang('en')}
                  className={`px-3 py-1 rounded-lg ${lang === 'en' ? 'bg-white text-indigo-600 shadow-xs' : 'text-slate-600'}`}
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => setLang('hi')}
                  className={`px-3 py-1 rounded-lg ${lang === 'hi' ? 'bg-white text-indigo-600 shadow-xs' : 'text-slate-600'}`}
                >
                  हिन्दी
                </button>
              </div>
            </div>

            <div className="flex flex-col space-y-1 text-sm font-semibold text-slate-700">
              <button
                onClick={() => { scrollToSection('features'); setMobileMenuOpen(false); }}
                className="text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between"
              >
                <span>{t.nav.features}</span>
                <span className="material-icons-round text-slate-400 text-sm">chevron_right</span>
              </button>
              <button
                onClick={() => { scrollToSection('community'); setMobileMenuOpen(false); }}
                className="text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between"
              >
                <span>{t.nav.forSchools}</span>
                <span className="material-icons-round text-slate-400 text-sm">chevron_right</span>
              </button>
              <button
                onClick={() => { scrollToSection('how-it-works'); setMobileMenuOpen(false); }}
                className="text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between"
              >
                <span>{t.nav.howItWorks}</span>
                <span className="material-icons-round text-slate-400 text-sm">chevron_right</span>
              </button>
              <button
                onClick={() => { scrollToSection('faq'); setMobileMenuOpen(false); }}
                className="text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between"
              >
                <span>{t.nav.faq}</span>
                <span className="material-icons-round text-slate-400 text-sm">chevron_right</span>
              </button>
              <button
                onClick={() => { scrollToSection('contact'); setMobileMenuOpen(false); }}
                className="text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between"
              >
                <span>{t.nav.contact}</span>
                <span className="material-icons-round text-slate-400 text-sm">chevron_right</span>
              </button>
            </div>

            <div className="pt-3 border-t border-slate-100 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => { setIsStoryOpen(true); setMobileMenuOpen(false); }}
                className="w-full py-2.5 px-3 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold text-xs flex items-center justify-center gap-2 hover:bg-indigo-100 transition-colors"
              >
                <span className="material-icons-round text-base text-indigo-600">play_circle</span>
                <span>{lang === 'hi' ? '60s एनिमेटेड स्टोरी देखें' : 'Watch 60-Sec Animated Story'}</span>
              </button>

              <button
                onClick={() => { handleGoogleSignIn(); setMobileMenuOpen(false); }}
                className="w-full py-3 bg-indigo-600 text-white font-bold text-sm rounded-xl flex items-center justify-center gap-2 shadow-md hover:bg-indigo-700"
              >
                <svg className="w-4 h-4 bg-white p-0.5 rounded-full" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z"></path>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                  <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
                <span>{lang === 'hi' ? 'गूगल से लॉग इन करें' : 'Sign in with Google'}</span>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Global Sign-In Error Alert */}
      {signInError && (
        <div className="max-w-3xl mx-auto mt-4 px-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-3 rounded-2xl text-sm flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <span className="material-icons-round text-red-500">error</span>
              <span>{signInError}</span>
            </div>
            <button onClick={() => setSignInError(null)} className="text-red-400 hover:text-red-700 ml-3">
              <span className="material-icons-round text-base">close</span>
            </button>
          </div>
        </div>
      )}

      {/* 2. HERO SECTION WITH CLEAN LIGHT BACKDROPS */}
      <section className="relative pt-10 pb-16 sm:pt-16 sm:pb-24 overflow-hidden bg-gradient-to-b from-white via-indigo-50/30 to-slate-50">
        {/* Soft Background Accents */}
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-indigo-100/40 rounded-full blur-3xl pointer-events-none -z-10"></div>
        <div className="absolute top-48 right-10 w-[400px] h-[300px] bg-amber-100/40 rounded-full blur-3xl pointer-events-none -z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Left Column: Clear, Friendly Copy & CTA */}
            <div className="lg:col-span-7 text-center lg:text-left space-y-6">
              
              {/* Friendly pill */}
              <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-200/80 text-indigo-800 text-xs sm:text-sm font-semibold shadow-xs">
                <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                <span>{t.hero.pill}</span>
              </div>

              {/* Big, Emotionally Clear Headline */}
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.2]">
                {t.hero.headlinePart1} <br />
                {t.hero.headlinePart2} <br />
                <span className="text-indigo-600">
                  {t.hero.headlinePart3}
                </span>
              </h1>

              {/* Human-first description */}
              <p className="text-base sm:text-lg text-slate-600 max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
                {t.hero.description}
              </p>

              {/* Big Sign In with Google CTA */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isSigningIn}
                  className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-base shadow-lg shadow-indigo-600/25 hover:shadow-indigo-600/35 transition-all flex items-center justify-center gap-3 cursor-pointer disabled:opacity-50 group"
                >
                  <svg className="w-5 h-5 bg-white p-0.5 rounded-full group-hover:scale-110 transition-transform" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z"></path>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                    <path fill="none" d="M0 0h48v48H0z"></path>
                  </svg>
                  <span>{t.hero.ctaPrimary}</span>
                  <span className="material-icons-round text-indigo-200 text-lg">arrow_forward</span>
                </button>

                <button
                  type="button"
                  onClick={() => setIsStoryOpen(true)}
                  className="w-full sm:w-auto px-6 py-4 rounded-2xl bg-white hover:bg-slate-50 border-2 border-indigo-200 text-indigo-700 hover:text-indigo-800 font-bold text-sm sm:text-base transition-all flex items-center justify-center gap-2.5 shadow-xs hover:shadow-md cursor-pointer group"
                >
                  <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                    <span className="material-icons-round text-base">play_arrow</span>
                  </span>
                  <span>{lang === 'hi' ? 'कहानी देखें (60 सेकंड)' : 'Watch 60-Sec Story'}</span>
                </button>

                <button
                  onClick={() => scrollToSection('community')}
                  className="w-full sm:w-auto px-6 py-4 rounded-2xl bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 hover:text-slate-900 font-bold text-sm sm:text-base transition-colors flex items-center justify-center gap-2 shadow-xs cursor-pointer"
                >
                  <span className="material-icons-round text-indigo-600 text-lg">visibility</span>
                  <span>{t.hero.ctaSecondary}</span>
                </button>
              </div>

              {/* Trust badges for non-tech users */}
              <div className="pt-3 flex flex-wrap items-center justify-center lg:justify-start gap-5 text-xs text-slate-500">
                <span className="flex items-center gap-1.5 font-medium">
                  <span className="material-icons-round text-emerald-600 text-base">check_circle</span>
                  {t.hero.badgeFree}
                </span>
                <span className="flex items-center gap-1.5 font-medium">
                  <span className="material-icons-round text-emerald-600 text-base">check_circle</span>
                  {t.hero.badgeNoDownload}
                </span>
                <span className="flex items-center gap-1.5 font-medium">
                  <span className="material-icons-round text-emerald-600 text-base">check_circle</span>
                  {t.hero.badgeAnyDevice}
                </span>
              </div>

            </div>

            {/* Right Column: Interactive App Showcase */}
            <div className="lg:col-span-5 relative">
              
              {/* Interactive Tab Switcher Header */}
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-3 bg-white p-1.5 rounded-2xl border border-slate-200 w-fit mx-auto lg:mx-0 shadow-sm">
                <button
                  type="button"
                  onClick={() => setActiveShowcaseTab('attendance')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                    activeShowcaseTab === 'attendance'
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <span className="material-icons-round text-sm">pie_chart</span>
                  <span>{t.showcase.tabAttendance}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveShowcaseTab('homework')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                    activeShowcaseTab === 'homework'
                      ? 'bg-amber-500 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <span className="material-icons-round text-sm">assignment</span>
                  <span>{t.showcase.tabHomework}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveShowcaseTab('broadcast')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                    activeShowcaseTab === 'broadcast'
                      ? 'bg-rose-600 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <span className="material-icons-round text-sm">campaign</span>
                  <span>{t.showcase.tabBroadcast}</span>
                </button>
              </div>

              {/* Showcase Screen Container */}
              <div className="relative rounded-3xl overflow-hidden border border-slate-200/90 bg-white shadow-xl shadow-indigo-100/50">
                
                {/* 1. ATTENDANCE TAB */}
                {activeShowcaseTab === 'attendance' && (
                  <div className="p-5 space-y-4 animate-fadeIn">
                    <div className="relative rounded-2xl overflow-hidden h-44 sm:h-52 border border-slate-100">
                      <img
                        src={images.heroClassroom}
                        alt="Classroom students taking morning roll call"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent"></div>
                      <div className="absolute top-3 right-3 bg-emerald-600 text-white text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm">
                        <span className="material-icons-round text-sm">task_alt</span>
                        <span>{t.showcase.rollCallFinished}</span>
                      </div>
                      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white">
                        <div>
                          <p className="text-xs text-slate-200">{t.showcase.gradeRoom}</p>
                          <p className="text-sm font-extrabold">{t.showcase.todaysRoll}</p>
                        </div>
                        <span className="text-emerald-300 text-lg font-black bg-slate-900/70 px-2.5 py-1 rounded-xl border border-emerald-400/30 backdrop-blur-xs">
                          96%
                        </span>
                      </div>
                    </div>

                    {/* Live Student Mini Toggles */}
                    <div className="bg-slate-50 rounded-2xl p-3 border border-slate-200 space-y-2">
                      <div className="flex items-center justify-between text-xs font-semibold text-slate-500 pb-1 border-b border-slate-200">
                        <span>{t.showcase.studentName}</span>
                        <span>{t.showcase.oneTapStatus}</span>
                      </div>
                      <div className="flex items-center justify-between py-1">
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                          Aarav Patel (Roll #01)
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-bold border border-emerald-200">
                          {t.showcase.present}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-1">
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                          Maya Chen (Roll #02)
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-bold border border-emerald-200">
                          {t.showcase.present}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-1">
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                          Liam Smith (Roll #03)
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 text-[11px] font-bold border border-rose-200">
                          {t.showcase.absentAlertSent}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* 2. HOMEWORK BOARD TAB */}
                {activeShowcaseTab === 'homework' && (
                  <div className="p-5 space-y-4 animate-fadeIn">
                    <div className="bg-amber-50/60 rounded-2xl p-4 border border-amber-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                          {t.showcase.mathGrade}
                        </span>
                        <span className="text-[11px] text-slate-500 font-medium">{t.showcase.dueTomorrow}</span>
                      </div>
                      <h4 className="text-base font-extrabold text-slate-900">{t.showcase.homeworkTitle}</h4>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        {t.showcase.homeworkDesc}
                      </p>

                      {/* Photo Attachment Preview */}
                      <div className="flex items-center gap-3 bg-white p-2.5 rounded-xl border border-slate-200 shadow-xs">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-xs flex-shrink-0">
                          <span className="material-icons-round text-lg">image</span>
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <p className="text-xs font-bold text-slate-800 truncate">Textbook_Page_42.jpg</p>
                          <p className="text-[10px] text-slate-500">{t.showcase.attachedByTeacher}</p>
                        </div>
                        <span className="text-[11px] text-emerald-700 font-bold bg-emerald-50 px-2 py-1 rounded border border-emerald-200">{t.showcase.viewable}</span>
                      </div>

                      {/* Encouraging AI Note */}
                      <div className="bg-white border border-indigo-100 p-2.5 rounded-xl flex items-center gap-2 text-xs text-slate-700 shadow-2xs">
                        <span className="material-icons-round text-amber-500 text-sm">auto_awesome</span>
                        <span><strong>{t.showcase.aiEncouragementLabel}</strong> "{t.showcase.aiEncouragementText}"</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-500 px-2">
                      <span>{t.showcase.parentsViewed}</span>
                      <span className="text-indigo-600 font-bold">{t.showcase.translatedPill}</span>
                    </div>
                  </div>
                )}

                {/* 3. BROADCAST ALERT TAB */}
                {activeShowcaseTab === 'broadcast' && (
                  <div className="p-5 space-y-4 animate-fadeIn">
                    <div className="bg-rose-50/70 rounded-2xl p-4 border border-rose-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-rose-600 text-white shadow-xs">
                          <span className="material-icons-round text-xs">warning</span>
                          {t.showcase.principalBroadcast}
                        </span>
                        <span className="text-[11px] text-rose-700 font-bold">{t.showcase.priorityHigh}</span>
                      </div>
                      <h4 className="text-base font-extrabold text-slate-900">{t.showcase.rainAdvisoryTitle}</h4>
                      <p className="text-xs text-slate-700 leading-relaxed">
                        {t.showcase.rainAdvisoryDesc}
                      </p>
                      
                      <div className="pt-2 border-t border-rose-200 flex items-center justify-between text-xs">
                        <span className="text-slate-600 font-medium">{t.showcase.deliveredFamilies}</span>
                        <span className="text-emerald-600 font-bold flex items-center gap-1">
                          <span className="material-icons-round text-sm">done_all</span>
                          {t.showcase.sentStatus}
                        </span>
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-2xl p-3.5 border border-slate-200 flex items-center justify-between text-xs text-slate-700">
                      <div className="flex items-center gap-2">
                        <span className="material-icons-round text-amber-500 text-base">notifications_active</span>
                        <span>{t.showcase.noSpamBadge}</span>
                      </div>
                      <span className="text-[11px] font-bold text-indigo-600">{t.showcase.principalProtected}</span>
                    </div>
                  </div>
                )}

                {/* Bottom interactive caption */}
                <div className="bg-slate-50 border-t border-slate-200 px-4 py-2.5 flex items-center justify-between text-xs text-slate-500">
                  <span className="flex items-center gap-1.5 font-medium">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    {t.showcase.clickTabsHint}
                  </span>
                  <button 
                    onClick={handleGoogleSignIn}
                    className="text-indigo-600 hover:text-indigo-700 font-bold transition-colors cursor-pointer"
                  >
                    {t.showcase.tryFreeMini}
                  </button>
                </div>

              </div>

            </div>

          </div>

        </div>
      </section>

      {/* 3. VISUAL ROLES SECTION: PRINCIPALS, TEACHERS & PARENTS */}
      <section id="community" className="py-20 bg-white border-t border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-widest font-black text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200/70">
              {t.roles.sectionTag}
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-4">
              {t.roles.title}
            </h2>
            <p className="text-slate-600 mt-3 text-sm sm:text-base">
              {t.roles.subtitle}
            </p>
          </div>

          {/* 3 Photo-Rich Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* 1. PRINCIPAL CARD */}
            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden hover:border-indigo-400 transition-all flex flex-col justify-between group shadow-sm hover:shadow-md">
              <div>
                {/* Visual Header Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={images.principalPortrait}
                    alt="Confident smiling school principal"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
                  <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-indigo-600 text-white shadow-sm">
                    {t.roles.forPrincipal}
                  </span>
                </div>

                <div className="p-7">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{t.roles.principalHeading}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-6">
                    {t.roles.principalDesc}
                  </p>

                  {/* Bullet points with icons */}
                  <div className="space-y-3 text-xs text-slate-600 font-medium">
                    <div className="flex items-start gap-2.5">
                      <span className="material-icons-round text-amber-500 text-base flex-shrink-0">check_circle</span>
                      <span>{t.roles.principalBullet1}</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="material-icons-round text-amber-500 text-base flex-shrink-0">check_circle</span>
                      <span>{t.roles.principalBullet2}</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="material-icons-round text-amber-500 text-base flex-shrink-0">check_circle</span>
                      <span>{t.roles.principalBullet3}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-7 pt-0">
                <button
                  onClick={handleGoogleSignIn}
                  className="w-full py-3 bg-indigo-50 hover:bg-indigo-600 text-indigo-700 hover:text-white text-xs font-bold rounded-xl border border-indigo-200 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>{t.roles.principalCta}</span>
                  <span className="material-icons-round text-sm">arrow_forward</span>
                </button>
              </div>
            </div>

            {/* 2. TEACHER CARD (FEATURED) */}
            <div className="bg-white border-2 border-indigo-600 rounded-3xl overflow-hidden hover:border-indigo-700 transition-all flex flex-col justify-between group shadow-md hover:shadow-lg relative">
              <div className="absolute top-4 right-4 z-10 bg-amber-400 text-slate-950 text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-sm">
                {t.roles.teacherFavorite}
              </div>

              <div>
                {/* Visual Header Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={images.teacherHelping}
                    alt="Teacher guiding student with schoolwork"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
                  <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-600 text-white shadow-sm">
                    {t.roles.forTeachers}
                  </span>
                </div>

                <div className="p-7">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{t.roles.teacherHeading}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-6">
                    {t.roles.teacherDesc}
                  </p>

                  {/* Bullet points with icons */}
                  <div className="space-y-3 text-xs text-slate-600 font-medium">
                    <div className="flex items-start gap-2.5">
                      <span className="material-icons-round text-emerald-600 text-base flex-shrink-0">check_circle</span>
                      <span>{t.roles.teacherBullet1}</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="material-icons-round text-emerald-600 text-base flex-shrink-0">check_circle</span>
                      <span>{t.roles.teacherBullet2}</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="material-icons-round text-emerald-600 text-base flex-shrink-0">check_circle</span>
                      <span>{t.roles.teacherBullet3}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-7 pt-0">
                <button
                  onClick={handleGoogleSignIn}
                  className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>{t.roles.teacherCta}</span>
                  <span className="material-icons-round text-sm">arrow_forward</span>
                </button>
              </div>
            </div>

            {/* 3. PARENT CARD */}
            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden hover:border-sky-400 transition-all flex flex-col justify-between group shadow-sm hover:shadow-md">
              <div>
                {/* Visual Header Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={images.parentChildHome}
                    alt="Parent helping child with homework at home"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
                  <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-sky-600 text-white shadow-sm">
                    {t.roles.forParents}
                  </span>
                </div>

                <div className="p-7">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{t.roles.parentHeading}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-6">
                    {t.roles.parentDesc}
                  </p>

                  {/* Bullet points with icons */}
                  <div className="space-y-3 text-xs text-slate-600 font-medium">
                    <div className="flex items-start gap-2.5">
                      <span className="material-icons-round text-sky-600 text-base flex-shrink-0">check_circle</span>
                      <span>{t.roles.parentBullet1}</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="material-icons-round text-sky-600 text-base flex-shrink-0">check_circle</span>
                      <span>{t.roles.parentBullet2}</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="material-icons-round text-sky-600 text-base flex-shrink-0">check_circle</span>
                      <span>{t.roles.parentBullet3}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-7 pt-0">
                <button
                  onClick={handleGoogleSignIn}
                  className="w-full py-3 bg-sky-50 hover:bg-sky-600 text-sky-700 hover:text-white text-xs font-bold rounded-xl border border-sky-200 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>{t.roles.parentCta}</span>
                  <span className="material-icons-round text-sm">arrow_forward</span>
                </button>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 4. VISUAL FEATURES GRID WITH CLEAN WHITE CARDS */}
      <section id="features" className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-widest font-black text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
              {t.features.tag}
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-4">
              {t.features.title}
            </h2>
            <p className="text-slate-600 mt-3 text-base">
              {t.features.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Visual Feature 1 */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-xs hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4 border border-amber-100">
                <span className="material-icons-round text-2xl">pie_chart</span>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">{t.features.f1Title}</h4>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {t.features.f1Desc}
              </p>
            </div>

            {/* Visual Feature 2 */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-xs hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4 border border-indigo-100">
                <span className="material-icons-round text-2xl">auto_fix_high</span>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">{t.features.f2Title}</h4>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {t.features.f2Desc}
              </p>
            </div>

            {/* Visual Feature 3 */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-xs hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4 border border-emerald-100">
                <span className="material-icons-round text-2xl">supervisor_account</span>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">{t.features.f3Title}</h4>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {t.features.f3Desc}
              </p>
            </div>

            {/* Visual Feature 4 */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-xs hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4 border border-rose-100">
                <span className="material-icons-round text-2xl">campaign</span>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">{t.features.f4Title}</h4>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {t.features.f4Desc}
              </p>
            </div>

            {/* Visual Feature 5 */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-xs hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center mb-4 border border-sky-100">
                <span className="material-icons-round text-2xl">translate</span>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">{t.features.f5Title}</h4>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {t.features.f5Desc}
              </p>
            </div>

            {/* Visual Feature 6 */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-xs hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center mb-4 border border-purple-100">
                <span className="material-icons-round text-2xl">verified_user</span>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">{t.features.f6Title}</h4>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {t.features.f6Desc}
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* 4B. THE OLD WAY VS. THE EDUGRAVITY WAY (BEFORE VS AFTER COMPARISON) */}
      <section className="py-20 bg-white border-t border-slate-200/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-widest font-black text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200/70">
              {t.comparison.tag}
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-4">
              {t.comparison.title}
            </h2>
            <p className="text-slate-600 mt-2 text-sm sm:text-base">
              {t.comparison.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            
            {/* Left: The Old Way */}
            <div className="bg-rose-50/50 border border-rose-200 rounded-3xl p-7 sm:p-8 flex flex-col justify-between shadow-xs">
              <div>
                <div className="flex items-center justify-between pb-5 border-b border-rose-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center">
                      <span className="material-icons-round text-2xl">sentiment_dissatisfied</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{t.comparison.oldWayTitle}</h3>
                      <p className="text-xs text-rose-700">{t.comparison.oldWaySub}</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-rose-100 text-rose-800 border border-rose-200">
                    {t.comparison.oldWayBadge}
                  </span>
                </div>

                <div className="mt-6 space-y-4 text-xs sm:text-sm text-slate-700">
                  <div className="flex items-start gap-3">
                    <span className="material-icons-round text-rose-500 text-lg flex-shrink-0 mt-0.5">close</span>
                    <span>{t.comparison.oldWayPoint1}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="material-icons-round text-rose-500 text-lg flex-shrink-0 mt-0.5">close</span>
                    <span>{t.comparison.oldWayPoint2}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="material-icons-round text-rose-500 text-lg flex-shrink-0 mt-0.5">close</span>
                    <span>{t.comparison.oldWayPoint3}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="material-icons-round text-rose-500 text-lg flex-shrink-0 mt-0.5">close</span>
                    <span>{t.comparison.oldWayPoint4}</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-rose-200 text-xs text-rose-800 font-medium">
                {t.comparison.oldWayResult}
              </div>
            </div>

            {/* Right: The EduGravity Way */}
            <div className="bg-emerald-50/50 border-2 border-emerald-500 rounded-3xl p-7 sm:p-8 flex flex-col justify-between shadow-sm relative">
              <div className="absolute -top-3 right-6 bg-emerald-600 text-white font-bold text-xs px-3 py-1 rounded-full shadow-xs">
                {t.comparison.newWayRecPill}
              </div>

              <div>
                <div className="flex items-center justify-between pb-5 border-b border-emerald-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                      <span className="material-icons-round text-2xl">sentiment_very_satisfied</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{t.comparison.newWayTitle}</h3>
                      <p className="text-xs text-emerald-700">{t.comparison.newWaySub}</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                    {t.comparison.newWayBadge}
                  </span>
                </div>

                <div className="mt-6 space-y-4 text-xs sm:text-sm text-slate-800">
                  <div className="flex items-start gap-3">
                    <span className="material-icons-round text-emerald-600 text-lg flex-shrink-0 mt-0.5">check_circle</span>
                    <span>{t.comparison.newWayPoint1}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="material-icons-round text-emerald-600 text-lg flex-shrink-0 mt-0.5">check_circle</span>
                    <span>{t.comparison.newWayPoint2}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="material-icons-round text-emerald-600 text-lg flex-shrink-0 mt-0.5">check_circle</span>
                    <span>{t.comparison.newWayPoint3}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="material-icons-round text-emerald-600 text-lg flex-shrink-0 mt-0.5">check_circle</span>
                    <span>{t.comparison.newWayPoint4}</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-emerald-200 flex items-center justify-between">
                <span className="text-xs text-emerald-800 font-bold">{t.comparison.newWayResult}</span>
                <button
                  onClick={handleGoogleSignIn}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer"
                >
                  {t.nav.startFree}
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4B-2. DEDICATED SPOTLIGHT: BUILT FOR BOTH SCHOOLS AND COACHING INSTITUTES */}
      <section id="coaching" className="py-20 bg-slate-900 text-white relative overflow-hidden">
        {/* Soft background accents */}
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-widest font-black text-indigo-400 bg-indigo-950/80 px-3.5 py-1.5 rounded-full border border-indigo-700/60 shadow-xs">
              {t.coachingSpotlight.tag}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 tracking-tight">
              {t.coachingSpotlight.title}
            </h2>
            <p className="text-slate-300 mt-3 text-sm sm:text-base leading-relaxed">
              {t.coachingSpotlight.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Card 1: Schools */}
            <div className="bg-slate-800/80 border border-slate-700 rounded-3xl p-7 sm:p-8 flex flex-col justify-between hover:border-indigo-500/60 transition-all shadow-xl">
              <div>
                <div className="flex items-center justify-between pb-6 border-b border-slate-700">
                  <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-600/20 text-indigo-400 flex items-center justify-center border border-indigo-500/30">
                      <span className="material-icons-round text-2xl">account_balance</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{t.coachingSpotlight.schoolsCardTitle}</h3>
                      <p className="text-xs text-indigo-300 font-medium">{t.coachingSpotlight.schoolsCardBadge}</p>
                    </div>
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-slate-700 text-slate-300 border border-slate-600">
                    K-12 Day Schools
                  </span>
                </div>

                <p className="mt-5 text-xs sm:text-sm text-slate-300 leading-relaxed">
                  {t.coachingSpotlight.schoolsCardDesc}
                </p>

                <div className="mt-6 space-y-3.5">
                  <div className="flex items-start gap-3 bg-slate-900/60 p-3.5 rounded-2xl border border-slate-700/60">
                    <span className="material-icons-round text-emerald-400 text-xl flex-shrink-0 mt-0.5">how_to_reg</span>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-white">{t.coachingSpotlight.schoolItem1Title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">{t.coachingSpotlight.schoolItem1Desc}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 bg-slate-900/60 p-3.5 rounded-2xl border border-slate-700/60">
                    <span className="material-icons-round text-amber-400 text-xl flex-shrink-0 mt-0.5">menu_book</span>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-white">{t.coachingSpotlight.schoolItem2Title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">{t.coachingSpotlight.schoolItem2Desc}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 bg-slate-900/60 p-3.5 rounded-2xl border border-slate-700/60">
                    <span className="material-icons-round text-rose-400 text-xl flex-shrink-0 mt-0.5">campaign</span>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-white">{t.coachingSpotlight.schoolItem3Title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">{t.coachingSpotlight.schoolItem3Desc}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 bg-slate-900/60 p-3.5 rounded-2xl border border-slate-700/60">
                    <span className="material-icons-round text-sky-400 text-xl flex-shrink-0 mt-0.5">supervisor_account</span>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-white">{t.coachingSpotlight.schoolItem4Title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">{t.coachingSpotlight.schoolItem4Desc}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-5 border-t border-slate-700 flex items-center justify-between">
                <span className="text-xs text-slate-400 font-medium">100% Free for Staff</span>
                <button
                  onClick={handleGoogleSignIn}
                  className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl transition-colors cursor-pointer"
                >
                  {t.roles.principalCta}
                </button>
              </div>
            </div>

            {/* Card 2: Coaching & Tuitions */}
            <div className="bg-slate-800/80 border-2 border-amber-500/70 rounded-3xl p-7 sm:p-8 flex flex-col justify-between hover:border-amber-400 transition-all shadow-xl relative">
              <div className="absolute -top-3.5 right-6 bg-amber-400 text-slate-950 font-black text-xs px-3.5 py-1 rounded-full shadow-md uppercase tracking-wider">
                JEE • NEET • Tuitions
              </div>

              <div>
                <div className="flex items-center justify-between pb-6 border-b border-slate-700">
                  <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center border border-amber-500/30">
                      <span className="material-icons-round text-2xl">science</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{t.coachingSpotlight.coachingCardTitle}</h3>
                      <p className="text-xs text-amber-300 font-medium">{t.coachingSpotlight.coachingCardBadge}</p>
                    </div>
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                    Batches & Academies
                  </span>
                </div>

                <p className="mt-5 text-xs sm:text-sm text-slate-300 leading-relaxed">
                  {t.coachingSpotlight.coachingCardDesc}
                </p>

                <div className="mt-6 space-y-3.5">
                  <div className="flex items-start gap-3 bg-slate-900/60 p-3.5 rounded-2xl border border-slate-700/60">
                    <span className="material-icons-round text-emerald-400 text-xl flex-shrink-0 mt-0.5">alarm_on</span>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-white">{t.coachingSpotlight.coachingItem1Title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">{t.coachingSpotlight.coachingItem1Desc}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 bg-slate-900/60 p-3.5 rounded-2xl border border-slate-700/60">
                    <span className="material-icons-round text-amber-400 text-xl flex-shrink-0 mt-0.5">photo_camera</span>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-white">{t.coachingSpotlight.coachingItem2Title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">{t.coachingSpotlight.coachingItem2Desc}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 bg-slate-900/60 p-3.5 rounded-2xl border border-slate-700/60">
                    <span className="material-icons-round text-sky-400 text-xl flex-shrink-0 mt-0.5">phonelink_lock</span>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-white">{t.coachingSpotlight.coachingItem3Title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">{t.coachingSpotlight.coachingItem3Desc}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 bg-slate-900/60 p-3.5 rounded-2xl border border-slate-700/60">
                    <span className="material-icons-round text-indigo-400 text-xl flex-shrink-0 mt-0.5">event_note</span>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-white">{t.coachingSpotlight.coachingItem4Title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">{t.coachingSpotlight.coachingItem4Desc}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-5 border-t border-slate-700 flex items-center justify-between">
                <span className="text-xs text-amber-300 font-medium">Batch Management Ready</span>
                <button
                  onClick={handleGoogleSignIn}
                  className="px-4 py-2.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold text-xs rounded-xl transition-colors cursor-pointer"
                >
                  {t.roles.teacherCta}
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Banner */}
          <div className="mt-12 bg-slate-800 border border-slate-700 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                <span className="material-icons-round text-lg">devices</span>
              </div>
              <p className="text-xs sm:text-sm text-slate-300 font-medium">
                {t.coachingSpotlight.bottomBanner}
              </p>
            </div>
            <button
              onClick={handleGoogleSignIn}
              className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs whitespace-nowrap shadow-md cursor-pointer transition-colors"
            >
              {t.coachingSpotlight.ctaButton}
            </button>
          </div>
        </div>
      </section>

      {/* 4C. SOCIAL PROOF & AUTHENTIC TESTIMONIALS */}
      <section className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-widest font-black text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200/70">
              {t.testimonials.tag}
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-4">
              {t.testimonials.title}
            </h2>
            <p className="text-slate-600 mt-2 text-sm sm:text-base">
              {t.testimonials.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Testimonial 1: Teacher */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200/90 flex flex-col justify-between shadow-xs hover:shadow-md transition-all">
              <div className="space-y-4">
                <div className="flex items-center gap-1 text-amber-500">
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                </div>
                <p className="text-xs sm:text-sm text-slate-700 italic leading-relaxed">
                  "{t.testimonials.t1Quote}"
                </p>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-800 font-bold flex items-center justify-center text-xs border border-amber-200 flex-shrink-0">
                  AR
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900">{t.testimonials.t1Author}</h4>
                  <p className="text-[11px] text-slate-500">{t.testimonials.t1Role}</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2: School Principal */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200/90 flex flex-col justify-between shadow-xs hover:shadow-md transition-all">
              <div className="space-y-4">
                <div className="flex items-center gap-1 text-amber-500">
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                </div>
                <p className="text-xs sm:text-sm text-slate-700 italic leading-relaxed">
                  "{t.testimonials.t2Quote}"
                </p>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-800 font-bold flex items-center justify-center text-xs border border-indigo-200 flex-shrink-0">
                  DK
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900">{t.testimonials.t2Author}</h4>
                  <p className="text-[11px] text-slate-500">{t.testimonials.t2Role}</p>
                </div>
              </div>
            </div>

            {/* Testimonial 3: Parent */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200/90 flex flex-col justify-between shadow-xs hover:shadow-md transition-all">
              <div className="space-y-4">
                <div className="flex items-center gap-1 text-amber-500">
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                </div>
                <p className="text-xs sm:text-sm text-slate-700 italic leading-relaxed">
                  "{t.testimonials.t3Quote}"
                </p>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-800 font-bold flex items-center justify-center text-xs border border-sky-200 flex-shrink-0">
                  SM
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900">{t.testimonials.t3Author}</h4>
                  <p className="text-[11px] text-slate-500">{t.testimonials.t3Role}</p>
                </div>
              </div>
            </div>

            {/* Testimonial 4: Coaching Academy Director */}
            <div className="bg-white p-6 rounded-3xl border-2 border-amber-200/80 flex flex-col justify-between shadow-xs hover:shadow-md transition-all bg-gradient-to-b from-amber-50/20 to-white">
              <div className="space-y-4">
                <div className="flex items-center gap-1 text-amber-500">
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                  <span className="material-icons-round text-lg">star</span>
                </div>
                <p className="text-xs sm:text-sm text-slate-700 italic leading-relaxed">
                  "{t.testimonials.t4Quote}"
                </p>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-200 text-amber-900 font-black flex items-center justify-center text-xs border border-amber-300 flex-shrink-0">
                  RV
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900">{t.testimonials.t4Author}</h4>
                  <p className="text-[11px] text-amber-800 font-semibold">{t.testimonials.t4Role}</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4D. STUDENT PRIVACY & SECURITY COMMITMENT */}
      <section className="py-14 bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 sm:p-10 shadow-xs">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              
              <div className="space-y-2 text-center md:text-left">
                <div className="inline-flex items-center gap-2 text-xs font-bold text-emerald-800 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
                  <span className="material-icons-round text-sm">lock</span>
                  {t.privacy.tag}
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900">{t.privacy.title}</h3>
                <p className="text-xs sm:text-sm text-slate-600 max-w-xl">
                  {t.privacy.desc}
                </p>
              </div>

              {/* 4 Trust Badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full md:w-auto">
                <div className="bg-white p-4 rounded-2xl border border-slate-200 text-center shadow-2xs">
                  <span className="material-icons-round text-emerald-600 text-2xl mb-1">no_cell</span>
                  <p className="text-[11px] font-bold text-slate-900">{t.privacy.b1Title}</p>
                  <p className="text-[10px] text-slate-500">{t.privacy.b1Sub}</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-slate-200 text-center shadow-2xs">
                  <span className="material-icons-round text-indigo-600 text-2xl mb-1">cloud_done</span>
                  <p className="text-[11px] font-bold text-slate-900">{t.privacy.b2Title}</p>
                  <p className="text-[10px] text-slate-500">{t.privacy.b2Sub}</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-slate-200 text-center shadow-2xs">
                  <span className="material-icons-round text-amber-600 text-2xl mb-1">badge</span>
                  <p className="text-[11px] font-bold text-slate-900">{t.privacy.b3Title}</p>
                  <p className="text-[10px] text-slate-500">{t.privacy.b3Sub}</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-slate-200 text-center shadow-2xs">
                  <span className="material-icons-round text-rose-600 text-2xl mb-1">block</span>
                  <p className="text-[11px] font-bold text-slate-900">{t.privacy.b4Title}</p>
                  <p className="text-[10px] text-slate-500">{t.privacy.b4Sub}</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 5. HOW IT WORKS: 3 SIMPLE STEPS */}
      <section id="how-it-works" className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          
          <span className="text-xs uppercase tracking-widest font-black text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200/70">
            {t.howItWorks.tag}
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-4">
            {t.howItWorks.title}
          </h2>
          <p className="text-slate-600 mt-2 text-sm max-w-xl mx-auto">
            {t.howItWorks.subtitle}
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            
            <div className="bg-white p-7 rounded-3xl border border-slate-200/90 shadow-xs relative">
              <div className="w-12 h-12 rounded-2xl bg-amber-400 text-slate-950 font-black text-xl flex items-center justify-center mb-5 shadow-sm">
                1
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t.howItWorks.step1Title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t.howItWorks.step1Desc}
              </p>
            </div>

            <div className="bg-white p-7 rounded-3xl border border-slate-200/90 shadow-xs relative">
              <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white font-black text-xl flex items-center justify-center mb-5 shadow-sm">
                2
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t.howItWorks.step2Title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t.howItWorks.step2Desc}
              </p>
            </div>

            <div className="bg-white p-7 rounded-3xl border border-slate-200/90 shadow-xs relative">
              <div className="w-12 h-12 rounded-2xl bg-emerald-600 text-white font-black text-xl flex items-center justify-center mb-5 shadow-sm">
                3
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t.howItWorks.step3Title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t.howItWorks.step3Desc}
              </p>
            </div>

          </div>

          <div className="mt-12">
            <button
              onClick={handleGoogleSignIn}
              disabled={isSigningIn}
              className="px-8 py-4 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-base shadow-lg shadow-indigo-600/25 hover:shadow-indigo-600/35 transition-all cursor-pointer inline-flex items-center gap-3"
            >
              <span>{t.howItWorks.ctaBottom}</span>
              <span className="material-icons-round text-lg">arrow_forward</span>
            </button>
          </div>

        </div>
      </section>

      {/* 6. FAQ SECTION */}
      <section id="faq" className="py-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-widest font-black text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200/70">
            {t.faq.tag}
          </span>
          <h2 className="text-3xl font-extrabold text-slate-900 mt-4">{t.faq.title}</h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, idx) => {
            const isOpen = activeFaq === idx;
            return (
              <div
                key={idx}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs transition-colors"
              >
                <button
                  type="button"
                  onClick={() => setActiveFaq(isOpen ? null : idx)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between text-base font-bold text-slate-900 hover:text-indigo-600 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <span className="material-icons-round text-amber-500 text-xl">help_outline</span>
                    <span>{faq.q}</span>
                  </span>
                  <span className={`material-icons-round text-xl text-slate-400 transition-transform ${isOpen ? 'rotate-180 text-indigo-600' : ''}`}>
                    expand_more
                  </span>
                </button>
                {isOpen && (
                  <div className="px-6 pb-6 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-4 pl-14">
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 7. CONTACT & INQUIRY BANNER */}
      <section id="contact" className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-indigo-700 via-indigo-600 to-slate-900 text-white border border-indigo-500 rounded-3xl p-8 sm:p-12 flex flex-col md:flex-row items-center justify-between gap-8 shadow-xl">
            <div className="space-y-3 text-center md:text-left">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-amber-300 bg-amber-400/20 px-3 py-1 rounded-full border border-amber-300/30">
                <span className="material-icons-round text-sm">support_agent</span>
                {t.contact.tag}
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-white">{t.contact.title}</h3>
              <p className="text-sm text-indigo-100 max-w-xl">
                {t.contact.desc}
              </p>
              <div className="pt-2 flex flex-wrap items-center justify-center md:justify-start gap-4 text-xs text-white font-semibold">
                <span className="flex items-center gap-1.5 bg-black/20 px-3 py-1.5 rounded-lg border border-white/20">
                  <span className="material-icons-round text-base text-amber-300">mail</span>
                  support@edugravity.app
                </span>
                <span className="flex items-center gap-1.5 bg-black/20 px-3 py-1.5 rounded-lg border border-white/20">
                  <span className="material-icons-round text-base text-emerald-300">shield</span>
                  {t.contact.cloudBadge}
                </span>
              </div>
            </div>

            <div className="flex-shrink-0">
              <button
                onClick={handleGoogleSignIn}
                className="px-6 py-4 bg-white hover:bg-slate-50 text-slate-900 font-extrabold text-sm rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer flex items-center gap-2.5"
              >
                <span>{t.contact.btnCta}</span>
                <span className="material-icons-round text-base text-indigo-600">arrow_forward</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 8. SIMPLE WARM FOOTER */}
      <footer className="py-10 bg-white border-t border-slate-200 text-slate-500 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-xs font-black">
              E
            </div>
            <span className="text-slate-900 font-bold text-sm">EduGravity</span>
            <span>{t.footer.tagline}</span>
          </div>

          <div className="flex items-center gap-6 font-semibold flex-wrap justify-center">
            <button onClick={() => scrollToSection('features')} className="hover:text-indigo-600 transition-colors cursor-pointer">
              {t.nav.features}
            </button>
            <button onClick={() => scrollToSection('community')} className="hover:text-indigo-600 transition-colors cursor-pointer">
              {t.nav.forSchools}
            </button>
            <button onClick={() => scrollToSection('faq')} className="hover:text-indigo-600 transition-colors cursor-pointer">
              {t.nav.faq}
            </button>
            <a href="/privacy" className="hover:text-indigo-600 transition-colors cursor-pointer">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:text-indigo-600 transition-colors cursor-pointer">
              Terms of Service
            </a>
            <button onClick={handleGoogleSignIn} className="text-indigo-600 hover:text-indigo-700 font-bold transition-colors cursor-pointer">
              {t.nav.startFree}
            </button>
          </div>
        </div>
        <div className="text-center text-[11px] text-slate-400 mt-6">
          © {new Date().getFullYear()} EduGravity. {t.footer.copyright}
        </div>
      </footer>

      {/* Interactive 60-Second Animated Doodle Story Tour */}
      <AnimatedStoryModal
        isOpen={isStoryOpen}
        onClose={() => setIsStoryOpen(false)}
        lang={lang}
        onStartFree={handleGoogleSignIn}
      />

    </div>
  );
};
